import { parseISO, startOfDay, format, differenceInYears } from 'date-fns';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { E164_PATTERN } from '@vagusx/shared';

/**
 * Format a phone number for display via libphonenumber-js.
 * US numbers → national format ("(415) 555-1234"); non-US numbers keep their
 * country code ("+44 7911 123456"). Falls back to the raw value when unparseable.
 */
export function formatPhone(phone: string | null | undefined): string {
  if (!phone) return '';
  const parsed = parsePhoneNumberFromString(phone, 'US');
  if (!parsed) return phone;
  // Only use international format for a number that's confidently NON-US; default
  // (US, or an unresolved/placeholder region) stays national so we don't regress
  // US/seed numbers into a "+1 555 555 5555" form.
  return parsed.country && parsed.country !== 'US'
    ? parsed.formatInternational()
    : parsed.formatNational();
}

/**
 * Best-effort normalize a phone number to E.164 (`+` + digits).
 * Returns null when it can't confidently normalize. Handles already-E.164
 * input, US 10-digit, and US 11-digit (leading 1). Used to seed dialer
 * fields from stored numbers that may be in display format.
 */
export function toE164(phone: string | null | undefined): string | null {
  if (!phone) return null;
  const cleaned = phone.replace(/[^\d+]/g, '');
  if (E164_PATTERN.test(cleaned)) return cleaned;
  const digits = cleaned.replace(/\D/g, '');
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
  return null;
}

/**
 * Choose the number to seed the SIP "number to call" field with. Prefers the
 * server-derived primary in `dialablePhones` (already strict E.164, and split
 * out of any multi-number field like "909-584-7207 (home) 909-866-8002 (work)"
 * so it's directly dialable). Falls back to a best-effort E.164 of the raw
 * on-file value, then the raw value itself so inline validation can flag it,
 * then '' when there's nothing to seed. See the API's `dialablePhones`.
 */
export function pickInitialDialNumber(
  dialablePhones: string[] | undefined,
  phone: string | null | undefined
): string {
  return dialablePhones?.[0] ?? toE164(phone) ?? phone ?? '';
}

/**
 * Parse a date string safely using date-fns.
 * Uses parseISO + startOfDay to avoid UTC midnight timezone shift bugs.
 */
function parseLocalDate(date: string): Date {
  return startOfDay(parseISO(date));
}

/**
 * Format an ISO date string to a readable format.
 * E.g., "1990-01-15" -> "Jan 15, 1990"
 */
export function formatDate(date: string | null | undefined): string {
  if (!date) return '';

  try {
    const parsed = parseLocalDate(date);
    if (isNaN(parsed.getTime())) return date;

    return format(parsed, 'MMM d, yyyy');
  } catch {
    return date;
  }
}

/**
 * Compact US-style date — `MM/dd/yyyy` (e.g. "1990-01-15" → "01/15/1990").
 * Use for table cells / inline labels where vertical space is tight and
 * the full month name would crowd the layout. DOB is the canonical case.
 */
export function formatDateShort(date: string | null | undefined): string {
  if (!date) return '';

  try {
    const parsed = parseLocalDate(date);
    if (isNaN(parsed.getTime())) return date;

    return format(parsed, 'MM/dd/yyyy');
  } catch {
    return date;
  }
}

/**
 * Calculate age in years from date of birth.
 */
export function calculateAge(dateOfBirth: string): number {
  return differenceInYears(startOfDay(new Date()), parseLocalDate(dateOfBirth));
}

/**
 * Format a date for display as a relative time or date.
 */
export function formatDateTime(date: string | null | undefined): string {
  if (!date) return '';

  try {
    const parsed = parseISO(date);
    if (isNaN(parsed.getTime())) return date;

    return format(parsed, 'MMM d, yyyy h:mm a');
  } catch {
    return date;
  }
}

/**
 * The pieces needed to render an appointment in the timezone it will actually
 * happen in — the LOCATION's, not the viewer's.
 *
 * A practice can span timezones (an athenaOne practice routinely has departments
 * in several), and an instant rendered in the browser's zone is wrong by the
 * offset and can land on the wrong DAY: midnight Eastern reads as 9 PM the
 * previous evening in California. The department's local time is what the
 * patient was told, what the front desk says on the phone, and what the EHR
 * itself displays, so it is the one to show.
 *
 * `zoneLabel` is non-null only when the appointment's zone differs from the
 * viewer's, so a single-timezone practice — every tenant but one today — sees
 * exactly what it sees now, with no redundant "PST" on every row.
 *
 * Falls back to the viewer's zone when `timezone` is absent or not a zone Intl
 * accepts; an unknown timezone must not blank an appointment out of the list.
 */
export function appointmentDateParts(
  iso: string,
  timezone: string | null | undefined
): { month: string; day: string; year: string; time: string; zoneLabel: string | null } {
  const date = new Date(iso);
  const opts = (extra: Intl.DateTimeFormatOptions): Intl.DateTimeFormatOptions =>
    timezone ? { ...extra, timeZone: timezone } : extra;

  try {
    const viewerZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const differs = Boolean(timezone) && timezone !== viewerZone;
    return {
      month: date.toLocaleDateString('en-US', opts({ month: 'short' })),
      day: date.toLocaleDateString('en-US', opts({ day: 'numeric' })),
      year: date.toLocaleDateString('en-US', opts({ year: 'numeric' })),
      time: date.toLocaleTimeString([], opts({ hour: 'numeric', minute: '2-digit' })),
      zoneLabel: differs
        ? (date
            .toLocaleTimeString('en-US', opts({ timeZoneName: 'short' }))
            .split(' ')
            .pop() ?? null)
        : null,
    };
  } catch {
    // An unrecognised timezone makes Intl throw. Render in the viewer's zone
    // rather than dropping the row — a slightly wrong time beats a blank card.
    return {
      month: date.toLocaleDateString('en-US', { month: 'short' }),
      day: date.toLocaleDateString('en-US', { day: 'numeric' }),
      year: date.toLocaleDateString('en-US', { year: 'numeric' }),
      time: date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }),
      zoneLabel: null,
    };
  }
}
